import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Bell } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { getNotificationsUnreadCount } from '@/api/generated/notifications-notifications/notifications-notifications'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import { useUser } from '@/hooks/use-user'
import { NotificationsDropdown } from './NotificationsDropdown'

// Mirrors Header.tsx's module-local `triggerCls` so the bell matches the other
// top-nav items exactly (kept local to avoid widening Header's export surface).
const triggerCls =
  'inline-flex items-center gap-0.5 px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-white/80 hover:text-white transition-colors whitespace-nowrap cursor-pointer outline-none'

function Badge({ count }: { count: number }) {
  if (count <= 0) return null
  return (
    <span className="absolute -top-0.5 right-0 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold leading-none text-white ring-2 ring-navy">
      {count > 9 ? '9+' : count}
    </span>
  )
}

export function NotificationBell() {
  const { user, isAuthenticated } = useUser()
  const [open, setOpen] = useState(false)

  // Unread-notifications badge poll — clones the Messages badge poll exactly:
  // disabled when logged out; 60s interval + refetch-on-focus keep it fresh.
  // The /notifications page and the dropdown below invalidate this queryKey
  // on read/mark-all so the badge updates immediately on top of the poll.
  const unreadQuery = useQuery({
    queryKey: ['notifications', 'unreadCount'],
    queryFn: ({ signal }) => getNotificationsUnreadCount(signal),
    enabled: isAuthenticated && !!user,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  })
  const count = unreadQuery.data?.count ?? 0

  return (
    <>
      {/* Mobile (< sm): a plain link straight to /notifications — no popover,
          matching the link-only Messages envelope. */}
      <Link
        to="/notifications"
        className={`${triggerCls} relative sm:hidden`}
        aria-label="Notifications"
      >
        <Badge count={count} />
        <Bell size={22} />
      </Link>

      {/* Desktop (>= sm): the bell opens a recent-notifications popover.
          Opening does NOT mark read. Hide below sm with `max-sm:hidden` (a
          media-query variant) — a bare `hidden` loses to the `inline-flex` in
          triggerCls in the compiled CSS, which would leak the "Notifications"
          label onto mobile next to the icon-only bell above. */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger className={`${triggerCls} relative max-sm:hidden sm:inline-flex`}>
          <Badge count={count} />
          <span>Notifications</span>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-96 p-0">
          <NotificationsDropdown onClose={() => setOpen(false)} />
        </PopoverContent>
      </Popover>
    </>
  )
}
